import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from 'src/components/atoms/Avatar';
import Button, { ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Icon, IconColor } from 'src/components/atoms/Icon';
import Text, { TextVariant } from 'src/components/atoms/Text';
import Toggle from 'src/components/atoms/Toggle';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { IOwner } from 'src/features/owner/ts/interfaces/Owner';
import DisableOwnerModal from './DisableOwnerModal';
import { Col, ColText, Row, RowCenter, StyledContainer } from './styles';

interface OwnerRowProps {
  owner: IOwner;
  onUpdateStatus: (id: string, status: 'ACTIVE' | 'DISABLED') => Promise<void>;
  isUpdatingStatus: boolean;
}

const OwnerRow = ({ owner, onUpdateStatus, isUpdatingStatus }: OwnerRowProps) => {
  const isOwnerDisabled = owner.status !== 'ACTIVE';
  const navigate = useNavigate();
  const [showConfirmDisableOwnerModal, setShowConfirmDisableOwnerModal] = useState(false);
  return (
    <StyledContainer>
      <Row>
        <Col>
          <Avatar
            src={'assets/images/icon-owner-white.svg'}
            isDisabled={isOwnerDisabled}
            backgroundColor="#6A93CA"
            imgHeight="60%"
          />
        </Col>
        <ColText>
          <Text
            className="small-margin"
            isBold
            color={isOwnerDisabled ? 'gray600' : 'initial'}
            style={{ marginBottom: '0.25rem' }}
            data-testid="owner-list-owner-name"
          >
            {owner.name}
          </Text>
          <Text
            variant={TextVariant.small}
            color={isOwnerDisabled ? 'gray600' : 'gray700'}
            data-testid="owner-list-owner-address"
          >
            {owner.address ? ` ${owner.address}` : ''}
            {owner.city ? ` ${owner.city}` : ''}
            {owner.state ? `, ${owner.state}` : ''}
            {owner.zip ? ` ${owner.zip}` : ''}
          </Text>
          <br />
          <Text isBold color="gray600" data-testid="owner-list-property-count">
            Properties: {owner.linkedPropertiesCount || 0}
          </Text>
        </ColText>
      </Row>
      <RowCenter>
        <Button
          name="gear icon"
          variant={ButtonVariant.ghost}
          size={ButtonSize.medium}
          onClick={() => navigate(`/owners/${owner.id}/edit`)}
        >
          <Icon icon="gear" color={isOwnerDisabled ? IconColor.muted : IconColor.primary} />
        </Button>

        <ToolTip
          content={!isOwnerDisabled ? 'Disable this owner' : 'Enable this owner'}
          direction={ToolTipDirection.bottom_fit}
        >
          <Toggle
            name="disabled"
            isActive={!isOwnerDisabled}
            onClick={() => {
              if (isUpdatingStatus) return;
              if (!isOwnerDisabled) {
                // instead of disabling directly, let's show a modal to confirm.
                setShowConfirmDisableOwnerModal(true);
                return;
              }
              onUpdateStatus(owner.id, isOwnerDisabled ? 'ACTIVE' : 'DISABLED');
            }}
          />
        </ToolTip>
      </RowCenter>
      <DisableOwnerModal
        linkedPropertiesCount={owner.linkedPropertiesCount ? owner.linkedPropertiesCount : 0}
        isOpen={showConfirmDisableOwnerModal}
        onConfirm={() => {
          onUpdateStatus(owner.id, 'DISABLED');
          setShowConfirmDisableOwnerModal(false);
        }}
        isUpdateLoading={isUpdatingStatus}
        closeModal={() => setShowConfirmDisableOwnerModal(false)}
      />
    </StyledContainer>
  );
};

export default OwnerRow;
